import React, { useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { FileTextIcon, BarChart2Icon, BriefcaseIcon } from 'lucide-react';
import { FlipbookModal } from './FlipbookModal';
interface Resource {
  label: string;
  icon: React.ElementType;
  pages: string[];
  title: string;
}
const resources: Resource[] = [{
  label: 'Executive Resume',
  icon: FileTextIcon,
  title: 'Executive Resume — Robert Maddox',
  pages: ['https://cdn.magicpatterns.com/uploads/fQs35j5VZ81uHZtVubjArZ/Resume.Maddox.png', 'https://cdn.magicpatterns.com/uploads/cLBriFMw6rgCx5Aj1uE7CP/Maddox._Resume.png']
}, {
  label: 'Executive Positioning Brief',
  icon: BarChart2Icon,
  title: 'Executive Positioning Brief — Robert Maddox',
  pages: ['https://cdn.magicpatterns.com/uploads/epeD4M5hRV1EECZi4jJt3i/Executive_Positioning_Brief_-_Robert_Maddox.png', 'https://cdn.magicpatterns.com/uploads/egTJTW8ws6Q9PNoaVMhYfM/EPB.E.png']
}, {
  label: 'Executive Cover Letter',
  icon: BriefcaseIcon,
  title: 'Executive Cover Letter — Robert Maddox',
  pages: ['https://cdn.magicpatterns.com/uploads/cTfPFAQgKY5PxxZ6fmtUsJ/Executive_Cover_Letter_-_Robert_Maddox.png']
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: 'easeOut'
    }
  }
};
export function AboutSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [activeResource, setActiveResource] = useState<Resource | null>(null);
  return <section id="about" ref={ref} className="py-20" style={{
    backgroundColor: '#ffffff'
  }} data-id="element-24">
      <div className="max-w-5xl mx-auto px-6" data-id="element-25">
        <motion.div variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="flex flex-col md:flex-row gap-12 md:gap-16" data-id="element-26">
          {/* Left: Heading */}
          <motion.div variants={itemVariants} className="md:w-56 flex-shrink-0" data-id="element-27">
            <div style={{
            borderLeft: '3px solid var(--gold)',
            paddingLeft: '16px'
          }} data-id="element-28">
              <h2 className="font-display" style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(26px, 3.5vw, 34px)',
              fontWeight: 700,
              letterSpacing: '0.03em',
              lineHeight: 1.2,
              color: '#0d1b4b'
            }} data-id="element-29">
                About Me
              </h2>
            </div>
          </motion.div>

          {/* Right: Content */}
          <div className="flex-1 flex flex-col gap-6" data-id="element-30">
            <motion.p variants={itemVariants} style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            color: '#4b5563',
            lineHeight: '1.85'
          }} data-id="element-31">
              Results-driven operations leader with{' '}
              <span style={{
              color: '#0d1b4b',
              fontWeight: 600
            }} data-id="element-32">
                20+ years of proven success
              </span>{' '}
              in aviation maintenance, manufacturing, and cross-functional
              program management. Expert at leading large technical teams,
              improving production efficiency, and ensuring compliance with
              strict safety and regulatory standards.
            </motion.p>

            <motion.p variants={itemVariants} style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            color: '#4b5563',
            lineHeight: '1.85'
          }} data-id="element-33">
              Demonstrated ability to manage{' '}
              <span style={{
              color: 'var(--gold)',
              fontWeight: 600
            }} data-id="element-34">
                multi-aircraft maintenance programs
              </span>
              , develop process improvements, and deliver mission-critical
              outcomes under demanding conditions.
            </motion.p>

            <motion.p variants={itemVariants} style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            color: '#4b5563',
            lineHeight: '1.85'
          }} data-id="element-35">
              Adept at aligning{' '}
              <span style={{
              color: '#0d1b4b',
              fontWeight: 600
            }} data-id="element-36">
                operational performance with strategic objectives
              </span>{' '}
              and contractual requirements — consistently delivering measurable
              results in high-accountability environments.
            </motion.p>

            {/* Divider */}
            <motion.div variants={itemVariants} className="h-px w-full" style={{
            background: 'linear-gradient(to right, rgba(201,150,62,0.5), transparent)'
          }} data-id="element-37" />

            {/* Professional Resources */}
            <motion.div variants={itemVariants} data-id="element-38">
              <p className="mb-4 font-semibold" style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#0d1b4b',
              letterSpacing: '0.02em'
            }} data-id="element-39">
                Professional Resources
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-40">
                {resources.map(({
                label,
                icon: Icon,
                pages,
                title
              }) => <motion.button key={label} onClick={() => setActiveResource({
                label,
                icon: Icon,
                pages,
                title
              })} whileTap={{
                scale: 0.95
              }} whileHover={{
                scale: 1.03,
                y: -2
              }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-200" style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 500,
                backgroundColor: '#f0f4ff',
                border: '1px solid rgba(13,27,75,0.2)',
                color: '#0d1b4b',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }} data-id="element-41">
                    <Icon size={14} style={{
                  color: 'var(--gold)',
                  flexShrink: 0
                }} data-id="element-42" />
                    {label}
                  </motion.button>)}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <FlipbookModal isOpen={activeResource !== null} onClose={() => setActiveResource(null)} title={activeResource?.title ?? ''} pages={activeResource?.pages ?? []} data-id="element-43" />
    </section>;
}